<template>
  <div
    v-if="!sendCartOpen"
    class="actions-container d-inline-flex flex-row-reverse align-center"
    :class="{ 'actions-container-expand': isExpanded() }"
  >
    <i @click.stop="toggleExpandMenu" class="icon-forms dont-mark" v-b-tooltip.bottom :title="titles.expand"></i>
    <div v-if="!isExpanded()" class="d-flex flex-row-reverse">
      <i @click.stop="removeCart" v-if="isCartOwner" class="icon-trash" v-b-tooltip.bottom :title="titles.remove"></i>
      <i @click.stop="$emit('sendCart', true)" class="icon-SEND" v-b-tooltip.bottom :title="titles.send"></i>
      <i @click="goToActive" class="icon-vue_image_medium" v-b-tooltip.bottom :title="titles.active"></i>
      <i
        @click.stop="$emit('edit')"
        v-if="isCartOwner"
        class="icon-PENCIL edit-cart-button"
        v-b-tooltip.bottom
        :title="titles.edit"
      ></i>
      <i
        @click.stop="setActive"
        class="dont-mark"
        :class="{
          'star-full icon-favoris_on': isActiveCart,
          'icon-favoris_off': !isActiveCart,
          pulseAnimationIn: isPulseAnimation(),
        }"
        v-b-tooltip.bottom
        :title="setActiveTooltip"
      ></i>
    </div>
    <div v-if="isExpanded()" class="d-flex flex-row-reverse">
      <a :download="cart.name + '.csv'" @click="download" ref="downloadLink" target="_blank">
        <i class="icon-clef_Gecol align-middle" v-b-tooltip.bottom :title="exportCartText"></i>
      </a>
      <TextNoteLink
        :textNote="textNote"
        :resource="cart"
        v-b-tooltip.bottom
        :title="titles.edit"
        icon="Notice_Add_Notes"
        v-if="textNote && !isPublishRequested && isContributor"
      ></TextNoteLink>
      <TextNoteLink
        type="cart"
        :resource="cart"
        v-b-tooltip.bottom
        :title="titles.create"
        icon="Notice_New"
        v-if="!textNote && isContributor"
      ></TextNoteLink>
      <a @click.stop.prevent="share()" v-b-tooltip.bottom :title="titles.share">
        <i class="icon-Share_intern"></i>
      </a>
      <PrintLink link="cart" :artwork="cart" :title="titles.print"></PrintLink>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import Carts from 'api/carts';
import TextNoteLink from './TextNoteLink.vue';
import PrintLink from './PrintLink.vue';
import abilitiesMixin from 'mixins/abilitiesMixin';
import { parse } from 'json2csv';

export default {
  name: 'CartActions',
  props: {
    cart: {
      type: Object,
      required: true,
    },
    sendCartOpen: {
      type: Boolean,
      required: true,
    },
  },
  mixins: [abilitiesMixin],
  components: {
    TextNoteLink,
    PrintLink,
  },
  computed: {
    ...mapGetters('carts', ['activeCart']),
    ...mapState('carts', ['pulseCartId', 'expandedCartId']),
    ...mapState('auth', ['currentUser']),
    textNote() {
      return this.cart.text_note;
    },
    isContributor() {
      return this.$can('edit_all', 'text_note') || this.$can('edit_own', 'text_note');
    },
    isPublishRequested() {
      const { status = undefined } = this.textNote || {};
      return status === 'publish_requested';
    },
    isActiveCart() {
      if (this.activeCart) return this.cart.id === this.activeCart.id;

      return false;
    },
    isCartOwner() {
      return this.cart.user_id === this.currentUser.id;
    },
    exportCartText() {
      return `${this.$gettext('Export clés gcoll2 de')} ${this.cart.name}`;
    },
    downloadBlob() {
      const data = this.cart.artwork_ids.map((id) => {
        return { id, blank: '' };
      });
      const fields = data.length > 0 ? ['id', 'blank'] : [];
      const options = {
        header: false,
        delimiter: ';',
        fields,
        quote: '',
        eol: '\r\n',
      };

      const blob = new Blob([parse(data, options)], { type: `data:text/csv;charset=utf-8;` });
      return blob;
    },
    setActiveTooltip() {
      if (!this.isActiveCart) {
        return this.$gettext('Activer cette sélection');
      }
      return false;
    },
    titles() {
      return {
        expand: this.$gettext('Autres fonctions'),
        remove: this.$gettext('Supprimer'),
        active: this.$gettext('Afficher la sélection'),
        edit: this.$gettext('Modifier la notice rédactionnelle'),
        create: this.$gettext('Créer une nouvelle notice rédactionnelle'),
        share: this.$gettext('Partager'),
        print: this.$gettext('Imprimer toutes les oeuvres'),
        send: this.$gettext('Envoyer'),
      };
    },
  },
  methods: {
    ...mapMutations('carts', ['assign']),
    ...mapActions('carts', ['summary']),
    setActive() {
      this.$store.dispatch('carts/setActive', this.cart);
    },
    goToActive() {
      this.$store.dispatch('carts/setActive', this.cart);

      const query = this.$route.query;
      this.$router.push({ name: 'Artworks', query: { ...query, favorites: this.cart.id, page: 1 } });
    },
    removeCart() {
      let confirmText = this.$gettext('Confirmez la suppression de la sélection en cours');
      if (confirm(confirmText)) {
        this.$store.dispatch('carts/remove', this.cart);
      }
    },
    isExpanded() {
      return this.expandedCartId === this.cart.id;
    },
    isPulseAnimation() {
      return this.pulseCartId === this.cart.id;
    },
    toggleExpandMenu() {
      if (this.isExpanded()) {
        this.assign({ name: 'expandedCartId', data: null });
      } else {
        this.assign({ name: 'expandedCartId', data: this.cart.id });
      }
    },
    share() {
      this.assign({ name: 'shareCart', data: this.cart });
      this.$root.$emit('bv::show::modal', 'cartShareModal');
    },
    download(e) {
      const filename = this.$refs.downloadLink.attributes.download.value;
      const blob = this.downloadBlob;

      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(blob, filename);
      } else {
        var link = document.createElement('a');
        link.setAttribute('href', window.URL.createObjectURL(blob));
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      this.summary({ id: this.cart.id, type: 'export' });
    },
  },
};
</script>
<style lang="less">
.actions-container {
  border: 2px solid transparent;
  padding: 5px 2px;

  &.actions-container-expand {
    border: 2px solid @chalky;
    background-color: @white;
    padding: 5px 2px;
  }
}
</style>
